// Copyright 2020-2021 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ControlBarButton,
  Phone,
  Modal,
  ModalBody,
  ModalHeader,
  ModalButton,
  ModalButtonGroup,
  useLogger,
  useMeetingManager,
  useRosterState
} from 'amazon-chime-sdk-component-library-react';

import { endMeeting, stopLiveTranscription } from '../../utils/api';
import { StyledP } from './Styled';
import { useAppState } from '../../providers/AppStateProvider';
import routes from '../../constants/routes';
import { useNavigation } from '../../providers/NavigationProvider';

const EndMeetingControl: React.FC = () => {
  const logger = useLogger();
  const [showModal, setShowModal] = useState(false);
  const toggleModal = (): void => setShowModal(!showModal);
  const navigate = useNavigate();
  const meetingManager = useMeetingManager();
  const { meetingId } = useAppState();
  const { roster } = useRosterState();
  const { showCaption, toggleCaption } = useNavigation();

  const leaveMeeting = async (): Promise<void> => { 
    if(Object.keys(roster).length <= 1){
      endMeetingForAll();
    }else {
      await meetingManager.leave();
      navigate(routes.HOME);
    }
  };

  const endMeetingForAll = async (): Promise<void> => {
    try {
      const meetingUUID = meetingManager.meetingId;
      if (meetingUUID && meetingId) {
        await stopLiveTranscription(meetingUUID);
        if(showCaption){
          toggleCaption();
        }
        await meetingManager.leave();
        await endMeeting(meetingUUID, meetingId);
        navigate(routes.HOME);
      }
    } catch (e) {
      logger.error(`Could not end meeting: ${e}`);
    }
  };

  return (
    <>
      <ControlBarButton icon={<Phone />} onClick={toggleModal} label="Leave" />
      {showModal && (
        <Modal size="md" onClose={toggleModal} rootId="modal-root">
          <ModalHeader title="End Meeting" />
          <ModalBody>
            <StyledP>
              Leave meeting or you can end the meeting for all. The meeting
              cannot be used once it ends.
            </StyledP>
          </ModalBody>
          <ModalButtonGroup
            primaryButtons={[
              <ModalButton
                key="end-meeting-for-all"
                onClick={endMeetingForAll}
                variant="primary"
                label="End meeting for all"
                closesModal
              />,
              <ModalButton
                key="leave-meeting"
                onClick={leaveMeeting}
                variant="primary"
                label="Leave Meeting"
                closesModal
              />,
              <ModalButton key="cancel-meeting-ending" variant="secondary" label="Cancel" closesModal />,
            ]}
          />
        </Modal>
      )}
    </>
  );
};

export default EndMeetingControl;
